import LearnPage from './LearnPage';
import SeeItLive from './SeeItLive';

const FAQS = [
  {
    q: 'What is a CLO ETF?',
    a: 'A Collateralized Loan Obligation (CLO) is a single security backed by a pool of hundreds of senior-secured leveraged corporate loans, cut into rated tranches (AAA down to unrated equity). A CLO ETF buys those tranches and trades on the exchange like a stock. The $1.3 trillion CLO market was institution-only until Janus Henderson launched JAAA in 2020; CLO ETFs have since passed $50 billion in assets.',
  },
  {
    q: 'Why do CLO ETFs pay more than bond funds with the same rating?',
    a: 'Two reasons. First, the underlying loans go to below-investment-grade companies, so they carry higher coupons than investment-grade bonds — but the tranche structure insulates the AAA slice from those defaults. Second, the loans are floating rate, so when the Fed raises rates the coupons reset higher instead of the price falling. AAA CLO ETFs have typically yielded 4.5–5%; BBB-B mezzanine funds 6–7%.',
  },
  {
    q: 'Why do RAAR and RAAY show a 0.00% dividend yield?',
    a: 'By design. RAAR (Reinvesting) rolls all interest back into NAV and never pays cash. RAAY (Annual) defers everything into one December distribution, so the trailing yield reads 0.00% for eleven months of the year. Neither fund is broken — they are earning the same coupons as the monthly-paying core fund RAAA; they just change WHEN you are paid. DiviDrip files them under non-dividend because there is no rolling payout to track.',
  },
  {
    q: 'What is the “nesting doll” structure?',
    a: 'Reckoner Capital runs each CLO portfolio as a three-layer fund-of-funds. RAAR holds ~99% of its assets in RAAY; RAAY holds ~99% in RAAA; only RAAA buys actual CLO tranches. The BBB-B suite mirrors it: RCLR → RCLY → RCLO. Each wrapper owns exactly one thing — the fund below it — which is how the manager changes the distribution schedule without running three separate loan books.',
  },
  {
    q: 'Is this a tax loophole?',
    a: 'It is a legal structuring choice around a real constraint: registered bond funds must distribute the interest they collect, which creates a monthly ordinary-income tax bill for holders in taxable accounts. The Reinvesting and Annual wrappers change the timing and character of what reaches you. In a Roth IRA or 401(k) the wrapper is redundant — all three versions compound identically because nothing is taxed. Consult a tax professional for your own situation; fund prospectuses spell out the mechanics.',
  },
  {
    q: 'Which CLO ETF should a Roth IRA investor pick?',
    a: 'Inside a Roth the distribution schedule does not matter for taxes, so pick on tranche risk and cost: the AAA funds (JAAA, PAAA, CLOA, RAAA) for a cash-plus anchor, or the mezzanine funds (JBBB, CLOZ, BCLO, RCLO) for more yield with more default sensitivity. If you never want to log in and reinvest cash, the Reinvesting wrappers (RAAR, RCLR) do it inside the fund. Watch liquidity — the Reckoner wrappers are small ($10–15M) and trade thinly.',
  },
];

const FUNDS = [
  ['JAAA', 'Janus Henderson AAA CLO', 'AAA', 'Monthly', '~4.9%'],
  ['PAAA', 'PGIM AAA CLO', 'AAA', 'Monthly', '~4.7%'],
  ['CLOA', 'iShares AAA CLO Active', 'AAA', 'Monthly', '~4.8%'],
  ['ICLO', 'Invesco AAA CLO Floating Rate Note', 'AAA', 'Monthly', '~4.9%'],
  ['RAAA', 'Reckoner Yield Enhanced AAA CLO', 'AAA', 'Monthly (core)', '~5%'],
  ['RAAY', 'Reckoner Yield Enhanced AAA CLO Annual', 'AAA', 'Once a year', '0.00% trailing'],
  ['RAAR', 'Reckoner Yield Enhanced AAA CLO Reinvesting', 'AAA', 'Never (reinvests)', '0.00% trailing'],
  ['JBBB', 'Janus Henderson B-BBB CLO', 'BBB-B', 'Monthly', '~6.4%'],
  ['CLOZ', 'Eldridge BBB-B CLO', 'BBB-B', 'Monthly', '~7.2%'],
  ['BCLO', 'iShares BBB-B CLO Active', 'BBB-B', 'Monthly', '~6.4%'],
  ['CLOI', 'VanEck CLO (multi-tranche IG)', 'AAA–BBB', 'Monthly', '~5.2%'],
  ['RCLO', 'Reckoner BBB-B CLO', 'BBB-B', 'Monthly (core)', '~6.5%'],
  ['RCLY', 'Reckoner BBB-B CLO Annual', 'BBB-B', 'Once a year', '0.00% trailing'],
  ['RCLR', 'Reckoner BBB-B CLO Reinvesting', 'BBB-B', 'Never (reinvests)', '0.00% trailing'],
];

export default function LearnCloEtfsNestingDoll() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };

  return (
    <LearnPage
      title="CLO ETFs Explained — JAAA to RAAR, the Waterfall, and Reckoner's Nesting-Doll Funds | TwylightCrow"
      description="What a Collateralized Loan Obligation ETF actually holds, why AAA CLO funds out-yield same-rated bonds, and the first breakdown of Reckoner's RAAR → RAAY → RAAA fund-of-funds structure that turns a monthly income fund into a 0.00%-yield compounding machine."
      canonical="https://twylightcrow.com/learn/clo-etfs-nesting-doll"
      guidePath="/learn/clo-etfs-nesting-doll"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · Floating-rate credit"
      h1="CLO ETFs — The Waterfall, the Yield Pick-Up, and the Nesting-Doll Funds That Pay 0.00%"
      datePublished="2026-09-16"
      jsonLd={jsonLd}
      share={{
        url: 'https://twylightcrow.com/api/share/og-page/learn-clo-etfs-nesting-doll',
        text: 'CLO ETFs explained — plus the first retail breakdown of Reckoner\u2019s nesting-doll funds: RAAR holds RAAY holds RAAA. Same loans, three payout schedules, one of them 0.00% by design:',
        hashtags: ['CLO', 'ETFs', 'FixedIncome', 'DividendInvesting'],
        key: 'learn-clo',
      }}
      related={[
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — High-Yield Vehicles Compared' },
        { to: '/learn/money-market-funds', label: 'Money Market Funds — the 5-Letter XX Ticker Guide' },
        { to: '/learn/hedged-vs-unhedged-etfs', label: 'Hedged vs Unhedged ETFs — Currency, Bonds, and the Tax Wrinkles' },
        { to: '/learn/preferred-stocks-explained', label: 'Preferred Stocks vs Common Stocks — When 6-10% Preferreds Beat Common' },
        { to: '/glossary#entry-ordinary-income', label: 'Glossary: Ordinary Income' },
      ]}
    >
      <p>
        If you scanned a ticker list this year and hit a run of ETFs with{' '}
        <strong>CLO</strong> in the name — some yielding 5%, some 7%, and two showing a
        flat <strong>0.00%</strong> — you found the fastest-growing corner of fixed
        income. Collateralized Loan Obligation ETFs went from zero to more than
        $50&nbsp;billion in five years by opening a $1.3&nbsp;trillion institutional
        market to anyone with a brokerage account. This guide explains what you
        actually own, why the yields beat same-rated bonds, and then walks through
        something we have not seen documented anywhere else for retail investors:
        Reckoner Capital&rsquo;s three-layer &ldquo;nesting-doll&rdquo; funds that turn a
        monthly income ETF into a zero-yield compounding vehicle.
      </p>

      <h2>What a CLO is, in one paragraph</h2>
      <p>
        A CLO is the corporate cousin of a mortgage-backed security. Instead of
        thousands of home loans, a manager pools several hundred{' '}
        <strong>leveraged loans</strong> — large, senior-secured, floating-rate bank
        loans made to companies rated below investment grade, typically to fund
        buyouts or expansion. The pool is then sliced into <strong>tranches</strong>{' '}
        with different claims on the cash.
      </p>

      <h2>The waterfall</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Tranche</th><th>Rating</th><th>Risk</th><th>Yield</th><th>Payment priority</th></tr>
        </thead>
        <tbody>
          <tr><td className="font-semibold">Senior</td><td>AAA / AA</td><td>Lowest</td><td>Lowest</td><td>Paid first from the loan pool</td></tr>
          <tr><td className="font-semibold">Mezzanine</td><td>A / BBB / BB / B</td><td>Moderate</td><td>Moderate–high</td><td>Paid after seniors are fully covered</td></tr>
          <tr><td className="font-semibold">Equity</td><td>Unrated</td><td>Highest</td><td>Highest (residual)</td><td>Last in line; absorbs the first defaults</td></tr>
        </tbody>
      </table>
      <p>
        As borrowers pay interest and principal, cash flows down the waterfall.
        When a company in the pool defaults, the loss hits the equity slice first,
        then mezzanine. The AAA tranche is insulated by everything below it — which
        is why AAA CLO tranches have essentially never defaulted, even through
        2008 and 2020, despite the &ldquo;junk&rdquo; borrowers underneath.
      </p>

      <h2>Why the yield pick-up exists</h2>
      <ul>
        <li><strong>Floating rate.</strong> Loan coupons reset with short-term rates. When the Fed hikes, a fixed-rate bond fund&rsquo;s price falls; a CLO fund&rsquo;s payout rises and its price barely moves.</li>
        <li><strong>Junk borrowers, investment-grade structure.</strong> The loans pay junk-bond-level interest, but the tranche structure lets the AAA slice keep an investment-grade rating. You collect part of the spread without the single-name default risk.</li>
        <li><strong>Complexity premium.</strong> Until 2020 nobody could buy this in an ETF. Institutions were paid extra for doing the work; the ETF wrapper passes some of that along.</li>
      </ul>
      <p>
        The result: AAA CLO ETFs have routinely yielded 4.5–5% while cash and
        Treasury bills paid less, and BBB-B mezzanine funds 6–7%.
      </p>

      <h2>The funds you will actually see</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Ticker</th><th>Fund</th><th>Tranche</th><th>Pays</th><th>Trailing yield</th></tr>
        </thead>
        <tbody>
          {FUNDS.map(([t, n, tr, pays, y]) => (
            <tr key={t}>
              <td className="font-mono font-semibold">{t}</td><td>{n}</td><td>{tr}</td><td>{pays}</td><td className="font-mono">{y}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-sm text-muted-foreground">
        Yields are approximate trailing figures at time of writing and float with
        short-term rates. Open any ticker in DiviDrip for the live number.
      </p>
      <SeeItLive ticker="JAAA" tab="insights" tone="emerald" label="See JAAA's CLO card live →" />

      <h2>The nesting doll: RAAR &rarr; RAAY &rarr; RAAA</h2>
      <p>
        Here is the part that made us write this guide. Pull up RAAR&rsquo;s
        holdings and you will find <strong>two positions</strong>: 99% in RAAY and 1% in
        a bank deposit account. Pull up RAAY and it is 99% RAAA. Only RAAA — the
        core Reckoner Yield Enhanced AAA CLO ETF — owns actual CLO tranches.
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Layer</th><th>AAA suite</th><th>BBB-B suite</th><th>What it does</th></tr>
        </thead>
        <tbody>
          <tr><td className="font-semibold">Core</td><td className="font-mono">RAAA</td><td className="font-mono">RCLO</td><td>Buys the CLO tranches. Pays monthly like every other CLO ETF.</td></tr>
          <tr><td className="font-semibold">Annual wrapper</td><td className="font-mono">RAAY</td><td className="font-mono">RCLY</td><td>Holds ~99% core fund. Defers all income into a single December distribution.</td></tr>
          <tr><td className="font-semibold">Reinvesting wrapper</td><td className="font-mono">RAAR</td><td className="font-mono">RCLR</td><td>Holds ~99% Annual wrapper. Reinvests everything into NAV — never pays cash.</td></tr>
        </tbody>
      </table>
      <p>
        Every layer owns exactly one thing: the fund beneath it. Same loans, same
        credit risk, same manager — the only variable is <em>when</em> the income
        reaches you: monthly, once a year, or never.
      </p>

      <h2>Why anyone would build this</h2>
      <p>
        Registered bond funds must pass through the interest they collect, and
        in a taxable brokerage account that interest is taxed as{' '}
        <strong>ordinary income</strong> — your highest bracket — every single year,
        whether you wanted the cash or not. That is a permanent drag on
        compounding for anyone who does not need the income yet.
      </p>
      <ul>
        <li><strong>Annual (RAAY / RCLY):</strong> one distribution instead of twelve keeps the capital compounding inside the fund for eleven months, and moves the taxable event to a single, plannable date.</li>
        <li><strong>Reinvesting (RAAR / RCLR):</strong> no cash ever leaves. Your return arrives as share-price appreciation, which is not taxed until <em>you</em> sell — and if you hold more than a year, potentially at long-term capital-gains rates rather than ordinary income.</li>
      </ul>
      <p>
        No other major sponsor — not BlackRock, Vanguard or Janus Henderson —
        offers the same wrapper set. Reckoner Capital Management (New York,
        founded 2024, backed by RedBird Capital) launched the four wrappers in
        February 2026 specifically for high-income investors holding credit
        outside retirement accounts.
      </p>

      <h2>How DiviDrip classifies them</h2>
      <p>
        The monthly payers — JAAA, CLOZ, RAAA, RCLO and the rest — are ordinary
        dividend ETFs in DiviDrip: real yield, real payout history, real
        ex-dates. The four Reckoner wrappers land in the{' '}
        <strong>non-dividend</strong> universe because there is no rolling payout to
        track; the Annual funds show a single December event and the Reinvesting
        funds never show one. Every CLO fund carries the blue <strong>CLO card</strong>{' '}
        on its Insights tab, which highlights where the fund sits in the
        waterfall, how it pays, and — for the Reckoner six — the full
        nesting-doll chain with one-click navigation between siblings.
      </p>
      <SeeItLive ticker="RAAR" tab="insights" tone="violet" label="See RAAR's nesting-doll card live →" />

      <h2>Where each version belongs</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Account</th><th>Best fit</th><th>Why</th></tr>
        </thead>
        <tbody>
          <tr><td className="font-semibold">Roth IRA / 401(k)</td><td>Any version — pick on tranche, not wrapper</td><td>Nothing is taxed inside the account, so monthly, annual and reinvesting compound identically. The wrapper is redundant; the Reinvesting version just saves you clicking &ldquo;reinvest.&rdquo;</td></tr>
          <tr><td className="font-semibold">Taxable brokerage, don&rsquo;t need income</td><td>Reinvesting (RAAR / RCLR)</td><td>Defers the tax bill until you sell and may convert ordinary income into capital gains. Check the prospectus and your tax professional.</td></tr>
          <tr><td className="font-semibold">Taxable brokerage, want one planned tax event</td><td>Annual (RAAY / RCLY)</td><td>Eleven months of untaxed compounding, one December distribution to plan around.</td></tr>
          <tr><td className="font-semibold">Living off the portfolio</td><td>Monthly core funds (JAAA, RAAA, CLOZ, RCLO…)</td><td>You want the cash. Floating-rate monthly income is the whole point.</td></tr>
        </tbody>
      </table>

      <h2>Risks that don&rsquo;t show up in the yield</h2>
      <ul>
        <li><strong>Mezzanine is not AAA.</strong> A BBB-B fund sits in the middle of the waterfall. In a real default wave it will mark down long before a AAA fund feels anything. The extra 1.5–2% is the price of that seat.</li>
        <li><strong>Liquidity.</strong> JAAA trades billions; the Reckoner wrappers hold $10–15M and trade thinly. Use limit orders and treat them as multi-year holds.</li>
        <li><strong>Rate cuts cut income.</strong> Floating rate works both ways — when the Fed eases, CLO coupons reset lower. The 0.00%-yield wrappers will simply compound more slowly.</li>
        <li><strong>Tax rules can change.</strong> The wrappers rely on current fund-taxation mechanics. Nothing here is tax advice.</li>
      </ul>

      <h2>The 60-second CLO check in DiviDrip</h2>
      <ol>
        <li>Open the ticker and go to <strong>Insights</strong>. The blue CLO card tells you the tranche tier and payout style at a glance.</li>
        <li>0.00% yield on a CLO fund? Look for the <strong>nesting-doll</strong> panel — it is a wrapper, not a broken fund. Click through to the core fund to see the real monthly yield.</li>
        <li>On the <strong>Stock Metrics</strong> tab, judge Reinvesting/Annual funds on price (NAV) growth, not dividend yield.</li>
        <li>Check the <strong>Fed &amp; Treasury Watch</strong> card on the Dashboard — falling short rates mean falling CLO coupons.</li>
      </ol>
    </LearnPage>
  );
}
